"use client";

import { Card, CircularProgress } from "@mui/material";
import React, { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import CampaignForm from "@/components/templates/CampaignForm";
import {
  buildCampaignCreateMultipartFormData,
  buildImageFormData,
  normalizeCreationData,
  normalizeUpdateData,
} from "@/components/templates/CampaignForm/form-data";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useToast from "@/contexts/toast-context";
import useCreateCampaignImage from "@/hooks/requests/campaign-image/use-create-campaignI-image";
import useRemoveCampaignImage from "@/hooks/requests/campaign-image/use-remove-campaign-image";
import useRemoveCampaignItem from "@/hooks/requests/campaign-items/use-remove-campaign-item";
import useRemoveCampaignSchedule from "@/hooks/requests/campaign-schedule/use-remove-campaign-schedule";
import useRemoveCampaignSegment from "@/hooks/requests/campaign-segment/use-remove-campaign-segment";
import useGetCampaignTemplates from "@/hooks/requests/campaign-template/use-get-campaign-templates";
import useCreateCampaign from "@/hooks/requests/campaigns/use-create-campaigns";
import useCreateCampaignSimpleMessage from "@/hooks/requests/campaigns/use-create-campaign-simple-message";
import useGetCampaignSimpleMessageById from "@/hooks/requests/campaigns/use-get-campaign-simple-message-by-id";
import useUpdateCampaignSimpleMessage from "@/hooks/requests/campaigns/use-update-campaign-simple-message";
import useGetCampaignById from "@/hooks/requests/campaigns/use-get-campaigns-by-id";
import useUpdateCampaign from "@/hooks/requests/campaigns/use-update-campaigns";
import useCreateTag from "@/hooks/requests/tags/use-create-tag";
import useLoading from "@/hooks/use-loading";
import { ImageData } from "@/interfaces/campaign.interface";
import { ICampaignSimpleMessage } from "@/interfaces/campaign-simple-message.interface";
import { CampaignFormPageMessages } from "@/messages/campaigns/campaign-form-page.messages";
import { CampaignType } from "@/types/campaign.types";

import { CampaignFormData, CampaignWithFormData } from "./types";

type Props = {
  messages: CampaignFormPageMessages;
  isEditing?: boolean;
};

function CampaignFormPage({ messages, isEditing = false }: Props) {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { lang } = useLanguage();
  const router = useNavigate();
  const _id = Number(campaignId);

  const {
    data: campaign,
    isLoading: isLoadingCampaign,
    error,
  } = useGetCampaignById({
    _id: isEditing ? _id : 0,
  });

  // Inicializar o templateIndex baseado no tipo de campanha (se estiver editando)
  const [templateIndex, setTemplateIndex] = useState<number>(() => {
    if (isEditing && campaign?.type !== undefined) {
      // Se for preço promocional, usar índice 1, se for cupom de desconto, usar índice 0
      return campaign.type === CampaignType.PromotionalPrice ? 1 : 0;
    }
    return 0; // Padrão para criação
  });

  const { data: templates, isLoading: isLoadingTemplate } = useGetCampaignTemplates();
  const { data: simpleMessageData, isLoading: isLoadingSimpleMessage } =
    useGetCampaignSimpleMessageById({ _id });
  const { update } = useUpdateCampaign();
  const { create } = useCreateCampaign();
  const { createCampaignSimpleMessage } = useCreateCampaignSimpleMessage();
  const { updateCampaignSimpleMessage } = useUpdateCampaignSimpleMessage();
  const { create: createContactTag } = useCreateTag();
  const { create: createImg } = useCreateCampaignImage();
  const { remove: removeCampaignImage } = useRemoveCampaignImage();
  const { remove: removeCampaignSchedule } = useRemoveCampaignSchedule();
  const { remove: removeCampaignItem } = useRemoveCampaignItem();
  const { remove: removeCampaignSegment } = useRemoveCampaignSegment();
  const { startLoading, endLoading, isLoading: isLoadingSubmit } = useLoading();
  const { showToast } = useToast();

  const BREAD_CRUMBS = [{ title: messages.breadCrumbTitle, href: ROUTE_URLS.campaigns(lang) }];
  const pageTitle = isEditing ? messages.formPageTitle : messages.createPageTitle;
  const shouldLoadError = !campaign && !!error && !isLoadingCampaign;

  const onSuccess = () => router(ROUTE_URLS.campaigns(lang));
  const onError = () => endLoading();

  const createTags = async (formData: CampaignFormData) => {
    const newContactTagPromises = formData.newContactTags.map((tagName) => {
      return createContactTag({ formData: { name: tagName } });
    });
    await Promise.all(newContactTagPromises);
  };

  const handleCreate = async (_formData: CampaignFormData) => {
    startLoading();

    const hasZeroPromoPrice = _formData.items.some((item) => item.promoPrice === 0);

    if (hasZeroPromoPrice) {
      showToast({ text: messages.errorZeroPromoPrice, type: "error" });
      endLoading();
      return;
    }

    if (_formData.type === CampaignType.PromotionalPrice && _formData.items.length === 0) {
      showToast({ text: messages.errorNoProductsForPromotionalPrice, type: "error" });
      endLoading();
      return;
    }

    if (
      _formData.type === CampaignType.DiscountCoupon &&
      (_formData.discountPercentage == null || _formData.discountPercentage <= 0)
    ) {
      showToast({ text: messages.errorZeroDiscountForCoupon, type: "error" });
      endLoading();
      return;
    }

    if (
      _formData.type === CampaignType.SimpleMessage &&
      (!_formData.simpleMessage || _formData.simpleMessage.trim().length === 0)
    ) {
      showToast({ text: messages.simpleMessageError, type: "error" });
      endLoading();
      return;
    }

    if (
      !_formData.isImmediateScheduling &&
      (!_formData.schedules || _formData.schedules.length === 0)
    ) {
      showToast({ text: messages.errorRequiredSchedules, type: "error" });
      endLoading();
      return;
    }

    await createTags(_formData);
    const normalizedCampaign = normalizeCreationData(_formData);
    const payloadForCreate = { ...normalizedCampaign, campaignImages: null };
    const firstNewImage = _formData.campaignImages?.find((img): img is ImageData => "file" in img);

    const successMessage = messages.campaignCreateSuccess;
    const errorMessage = messages.campaignCreateError;

    const formDataForCreate = firstNewImage
      ? buildCampaignCreateMultipartFormData(payloadForCreate, firstNewImage)
      : payloadForCreate;

    const response = await create({ formData: formDataForCreate, successMessage, errorMessage, onError });

    // Se for Mensagem Simples, criar o registro de SimpleMessage após criar a campanha
    if (_formData.type === CampaignType.SimpleMessage && _formData.simpleMessage?.trim()) {
      const payload = {
        campaignId: (response as { id: number }).id,
        messageText: _formData.simpleMessage!,
        hasActionButton: Boolean(_formData.hasButton),
        buttonText: _formData.hasButton ? _formData.buttonText : undefined,
        buttonLink: _formData.hasButton ? _formData.buttonLink : undefined,
      };
      await createCampaignSimpleMessage(payload);
    }

    onSuccess();
  };

  const handleUpdate = async (_formData: CampaignFormData) => {
    startLoading();

    const hasZeroPromoPrice = _formData.items.some((item) => item.promoPrice === 0);

    if (hasZeroPromoPrice) {
      showToast({ text: messages.errorZeroPromoPrice, type: "error" });
      endLoading();
      return;
    }

    const successMessage = messages.campaignUpdateSuccess;
    const errorMessage = messages.campaignUpdateError;

    if (
      _formData.type === CampaignType.DiscountCoupon &&
      (_formData.discountPercentage == null || _formData.discountPercentage <= 0)
    ) {
      showToast({ text: messages.errorZeroDiscountForCoupon, type: "error" });
      endLoading();
      return;
    }

    if (_formData.type === CampaignType.PromotionalPrice && _formData.items.length === 0) {
      showToast({ text: messages.errorNoProductsForPromotionalPrice, type: "error" });
      endLoading();
      return;
    }

    if (
      _formData.type === CampaignType.SimpleMessage &&
      (!_formData.simpleMessage || _formData.simpleMessage.trim().length === 0)
    ) {
      showToast({ text: messages.simpleMessageError, type: "error" });
      endLoading();
      return;
    }

    if (
      !_formData.isImmediateScheduling &&
      (!_formData.schedules || _formData.schedules.length === 0)
    ) {
      showToast({ text: messages.errorRequiredSchedules, type: "error" });
      endLoading();
      return;
    }

    await createTags(_formData);
    const formData = normalizeUpdateData(_formData);
    await update({ _id, formData, successMessage, errorMessage, onError });

    // Se for Mensagem Simples, atualizar o registro de SimpleMessage após atualizar a campanha
    if (_formData.type === CampaignType.SimpleMessage && _formData.simpleMessage?.trim()) {
      const payload = {
        campaignId: _id,
        messageText: _formData.simpleMessage,
        hasActionButton: Boolean(_formData.hasButton),
        buttonText: _formData.hasButton ? _formData.buttonText : undefined,
        buttonLink: _formData.hasButton ? _formData.buttonLink : undefined,
      };

      const existingSimpleMessageId =
        simpleMessageData?.id ??
        (campaign?.simpleMessage && typeof campaign.simpleMessage === "object"
          ? (campaign.simpleMessage as ICampaignSimpleMessage).id
          : undefined);

      if (existingSimpleMessageId) {
        await updateCampaignSimpleMessage(existingSimpleMessageId, payload);
      } else {
        await createCampaignSimpleMessage(payload);
      }
    }

    if (_formData.imagesToDelete && _formData.imagesToDelete.length > 0) {
      await Promise.all(
        _formData.imagesToDelete.map(async (imageId) => {
          await removeCampaignImage({ _id: imageId, onError });
        })
      );
    }

    if (_formData.removedSchedules?.length > 0) {
      await Promise.all(
        _formData.removedSchedules.map(async (scheduleId) => {
          await removeCampaignSchedule({ _id: scheduleId, onError });
        })
      );
    }

    if (_formData.removedItems?.length > 0) {
      await Promise.all(
        _formData.removedItems.map(async (itemId) => {
          await removeCampaignItem({ _id: itemId, onError });
        })
      );
    }

    if (_formData.removedSegments?.length > 0) {
      await Promise.all(
        _formData.removedSegments.map(async (segmentId) => {
          await removeCampaignSegment({ _id: segmentId, onError });
        })
      );
    }

    if (_formData.campaignImages && _formData.campaignImages.length > 0) {
      for (const image of _formData.campaignImages) {
        if ("file" in image) {
          const id = formData.id as number;
          const imageFormData = buildImageFormData(image as ImageData, id.toString());
          const errorMessage = messages.imageUpdateError;
          try {
            await createImg({ formData: imageFormData, errorMessage, onError });
          } catch (e) {
            // Continue updating campaign even if image upload fails
            console.warn("Failed to upload campaign image:", e);
          }
        }
      }
    }

    endLoading();
    router(ROUTE_URLS.campaigns(lang));
  };

  const handleSubmit = (_formData: CampaignFormData) => {
    if (isEditing) handleUpdate(_formData);
    if (!isEditing) handleCreate(_formData);
  };

  const isLoading =
    isLoadingCampaign || isLoadingTemplate || isLoadingSimpleMessage || isLoadingSubmit;

  const campaignWithSimpleMessage = useMemo(() => {
    if (!campaign) return campaign;

    // Se a campanha já tem simpleMessage aninhado, use esses dados
    if (campaign.simpleMessage && typeof campaign.simpleMessage === "object") {
      const simpleMsg = campaign.simpleMessage as unknown as ICampaignSimpleMessage;
      return {
        ...campaign,
        simpleMessage: simpleMsg.messageText ?? "",
        hasButton: Boolean(simpleMsg.hasActionButton),
        buttonText: simpleMsg.buttonText ?? "",
        buttonLink: simpleMsg.buttonLink ?? "",
      } as CampaignWithFormData;
    }

    // Fallback para dados vindos do hook separado
    if (simpleMessageData) {
      return {
        ...campaign,
        simpleMessage: simpleMessageData.messageText ?? "",
        hasButton: Boolean(simpleMessageData.hasActionButton),
        buttonText: simpleMessageData.buttonText ?? "",
        buttonLink: simpleMessageData.buttonLink ?? "",
      } as CampaignWithFormData;
    }

    return campaign as CampaignWithFormData;
  }, [campaign, simpleMessageData]);

  // Selecionar o template correto baseado no tipo de campanha
  const template = useMemo(() => {
    if (!templates?.items || templates.items.length === 0) return undefined;

    if (isEditing && campaign?.type !== undefined) {
      // Se estiver editando, selecionar o template baseado no tipo da campanha
      const targetIndex = campaign.type === CampaignType.PromotionalPrice ? 1 : 0;
      return templates.items[targetIndex] || templates.items[0];
    }

    // Se estiver criando, usar o templateIndex atual
    return templates.items[templateIndex] || templates.items[0];
  }, [templates?.items, templateIndex, isEditing, campaign?.type]);

  return (
    <PageLayout messages={messages} title={pageTitle} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <Suspense fallback={<CircularProgress />} isSuspended={isLoading}>
          {!shouldLoadError && (
            <CampaignForm
              campaign={campaignWithSimpleMessage as CampaignWithFormData}
              isEditing={isEditing}
              messages={messages}
              onSubmit={handleSubmit}
              isLoading={isLoading}
              setTemplateIndex={setTemplateIndex}
              template={template}
            />
          )}

          {!!shouldLoadError && (
            <MDBox mt={2}>
              <Card>
                <AlertBlock
                  title={messages.campaignNotFoundError}
                  description={error.message}
                  alt={messages.campaignNotFoundAlt}
                />
              </Card>
            </MDBox>
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}

export default CampaignFormPage;
